// src/components/pages/PointsLedgerPage.js
import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PlaceIcon from "@mui/icons-material/Place";
import StarsIcon from "@mui/icons-material/Stars";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import party from "party-js";
import { db } from "../../firebase";
import { getMainScrollContainer } from "../../utils/layoutShell";
import { groupReceiptsByCapsule } from "../../utils/points/groupReceipts";
import PromoRedeemModal from "../promo/PromoRedeemModal";

const TRIGGER_KEYS = {
  INSIDER_FREE_SIGNUP: "insider_free_signup",
  PRIVATE_TC_SENT: "private_tc_drop",
  PRIVATE_TC_RECIPIENT: "private_tc_recipient",
  PRIVATE_TC_NEW_RECIPIENT: "private_tc_new_recipient",
};

function asInt(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function fmtDate(d) {
  if (!d) return "";
  try {
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

function toDateMaybe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  const n = Number(v);
  if (Number.isFinite(n)) return new Date(n);
  const dt = new Date(String(v));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function plural(n, one, many) {
  return n === 1 ? one : many;
}

function deriveIsInsider(userDocData) {
  const tier = String(userDocData?.subscriptionData?.tier || "").toLowerCase().trim();
  const status = String(userDocData?.subscriptionData?.subscriptionStatus || "").toLowerCase().trim();
  return tier === "insider" && (status === "" || status === "active");
}

function describeReceipt(receipt) {
  const reason = receipt?.reason || "";
  const meta = receipt?.meta || {};
  const pts = asInt(receipt?.deltaPoints, 0);

  if (reason === "insider_free_signup") {
    return {
      title: "Insider activated",
      subtitle: pts > 0 ? `Insider bonus +${pts}` : "Welcome in. Your perks and points are ready.",
      icon: <WorkspacePremiumIcon />,
      pts,
    };
  }

  if (reason === "promo_code_redemption") {
    const code = String(meta?.code || "").trim();
    return {
      title: "Promo code redeemed",
      subtitle: code ? `You claimed ${code} 🎉` : "Bonus points added to your balance 🎉",
      icon: <WorkspacePremiumIcon />,
      pts,
    };
  }

  if (reason === "tc_drop") {
    return {
      title: "Time capsule buried",
      subtitle: "You dropped a capsule on the map 🕰️",
      icon: <PlaceIcon />,
      pts,
    };
  }

  if (reason === "tc_pickup") {
    const isNew = meta?.isNew === true;
    return isNew
      ? {
          title: "A new friend joined!",
          subtitle: "Someone new opened your capsule 🎉",
          icon: <PersonAddAltIcon />,
          pts,
        }
      : {
          title: "Your capsule was opened",
          subtitle: "A friend picked it up 👀",
          icon: <StarsIcon />,
          pts,
        };
  }

  if (reason === "tc_sent") {
    const recipientCount = asInt(meta?.recipientCount, 0);
    const newRecipientCount = asInt(meta?.newRecipientCount, 0);
    const perEventPts = asInt(meta?.perEventPts, 0);
    const perRecipientPts = asInt(meta?.perRecipientPts, 0);
    const perNewRecipientPts = asInt(meta?.perNewRecipientPts, 0);

    const dropEarned = Math.max(0, perEventPts);
    const recipientsEarned =
      recipientCount > 0 && perRecipientPts > 0 ? recipientCount * perRecipientPts : 0;
    const newRecipientsEarned =
      newRecipientCount > 0 && perNewRecipientPts > 0 ? newRecipientCount * perNewRecipientPts : 0;

    const parts = [];
    if (dropEarned > 0) parts.push(`Drop +${dropEarned}`);

    if (recipientsEarned > 0) {
      parts.push(`Recipients +${recipientsEarned} (${recipientCount}×${perRecipientPts})`);
    } else if (recipientCount > 0) {
      parts.push(`${recipientCount} ${plural(recipientCount, "recipient", "recipients")}`);
    }

    if (newRecipientsEarned > 0) {
      parts.push(`New recipients +${newRecipientsEarned} (${newRecipientCount}×${perNewRecipientPts})`);
    } else if (newRecipientCount > 0) {
      parts.push(`${newRecipientCount} new ${plural(newRecipientCount, "recipient", "recipients")}`);
    }

    if (parts.length === 0) parts.push("Nice drop.");

    return {
      title: "Time capsule buried",
      subtitle: parts.join(" • "),
      icon: <PlaceIcon />,
      pts,
    };
  }

  return {
    title: reason ? `Points awarded (${reason})` : "Points awarded",
    subtitle: "",
    icon: <StarsIcon />,
    pts,
  };
}

// Render a grouped capsule (drop + every per-recipient reward that shares its
// capsuleId) as one combined ledger row summing to the full total.
function describeCapsuleGroup(group) {
  const groupReceipts = Array.isArray(group?.receipts) ? group.receipts : [];
  const totalPts = asInt(group?.totalPts, 0);

  let dropPts = 0;
  let recipientCount = 0;
  let recipientPts = 0;
  let newRecipientCount = 0;

  groupReceipts.forEach((r) => {
    const rPts = asInt(r?.deltaPoints, 0);
    if (r?.reason === "tc_drop") {
      dropPts += rPts;
    } else if (r?.reason === "tc_pickup") {
      recipientCount += 1;
      recipientPts += rPts;
      if (r?.meta?.isNew === true) newRecipientCount += 1;
    }
  });

  const parts = [];
  if (dropPts > 0) parts.push(`Drop +${dropPts}`);
  if (recipientCount > 0) {
    parts.push(
      `${recipientCount} ${plural(recipientCount, "recipient", "recipients")} +${recipientPts}`
    );
  }
  if (newRecipientCount > 0) {
    parts.push(`${newRecipientCount} new ${plural(newRecipientCount, "friend", "friends")} 🎉`);
  }
  if (parts.length === 0) parts.push("You dropped a capsule on the map 🕰️");

  return {
    title: "Time capsule buried",
    subtitle: parts.join(" • "),
    icon: <PlaceIcon />,
    pts: totalPts,
  };
}

function StatCard({ label, value, icon }) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 1.8,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.62)
            : alpha(theme.colors.mediumBlue, 0.05),
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={(theme) => ({
            width: 32,
            height: 32,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.colors.mediumBlue, 0.24)
                : alpha(theme.colors.inssa, 0.14),
            color: theme.palette.mode === "dark" ? theme.colors.white : theme.colors.mediumBlue,
          })}
        >
          {icon}
        </Avatar>
      </Stack>
    </Paper>
  );
}

export default function PointsLedgerPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  const PAGE_SIZE = 15;
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [receipts, setReceipts] = React.useState([]);
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const [pointsTotal, setPointsTotal] = React.useState(0);
  const [pointsDisplay, setPointsDisplay] = React.useState(0);
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [isInsider, setIsInsider] = React.useState(false);
  const [triggersLoading, setTriggersLoading] = React.useState(true);
  const [triggersByKey, setTriggersByKey] = React.useState({});
  const [promoOpen, setPromoOpen] = React.useState(false);

  // Collapse per-capsule receipts (drop + per-recipient) into one ledger item.
  const ledgerItems = React.useMemo(() => groupReceiptsByCapsule(receipts), [receipts]);

  const totalElRef = React.useRef(null);
  const lastBurstRef = React.useRef(0);
  const initialScrollLockUntilRef = React.useRef(0);

  React.useLayoutEffect(() => {
    const resetScroll = () => {
      const scroller = getMainScrollContainer();
      if (scroller) {
        scroller.scrollTop = 0;
        scroller.scrollLeft = 0;
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    resetScroll();
    const rafId = requestAnimationFrame(resetScroll);
    return () => cancelAnimationFrame(rafId);
  }, []);

  React.useLayoutEffect(() => {
    initialScrollLockUntilRef.current = Date.now() + 1800;
  }, []);

  React.useLayoutEffect(() => {
    if (Date.now() > initialScrollLockUntilRef.current) return;

    const resetScroll = () => {
      const scroller = getMainScrollContainer();
      if (scroller) {
        scroller.scrollTop = 0;
        scroller.scrollLeft = 0;
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    resetScroll();
    const rafId = requestAnimationFrame(resetScroll);
    const timeoutId = setTimeout(resetScroll, 120);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [loading, triggersLoading, profileLoading, receipts.length]);

  React.useEffect(() => {
    if (!user?.uid) {
      setProfileLoading(false);
      setIsInsider(false);
      return;
    }

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        setIsInsider(deriveIsInsider(data));
        setProfileLoading(false);
      },
      () => {
        setIsInsider(false);
        setProfileLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  React.useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setErr("Please sign in to view your points.");
      return;
    }

    const qRef = query(collection(db, "users", user.uid, "award_receipts"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        const sum = rows.reduce((acc, row) => acc + asInt(row.deltaPoints, 0), 0);
        setReceipts(rows);
        setPointsTotal(Math.max(0, sum));
        setLoading(false);
        setErr(null);
      },
      () => {
        setErr("Could not load your points right now. Please try again.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  React.useEffect(() => {
    const ref = collection(db, "config", "points", "triggers");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const key = String(data.key || d.id || "").trim();
          if (!key) return;
          next[key] = { ...data, key };
        });
        setTriggersByKey(next);
        setTriggersLoading(false);
      },
      () => {
        setTriggersByKey({});
        setTriggersLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const getTriggerPointsIfEnabled = React.useCallback(
    (key) => {
      const t = triggersByKey?.[key];
      if (!t) return { enabled: false, points: 0 };
      const enabled = t.enabled !== false;
      const points = enabled ? Math.max(0, asInt(t.points, 0)) : 0;
      return { enabled, points };
    },
    [triggersByKey]
  );

  const insiderCfg = React.useMemo(
    () => getTriggerPointsIfEnabled(TRIGGER_KEYS.INSIDER_FREE_SIGNUP),
    [getTriggerPointsIfEnabled]
  );
  const dropCfg = React.useMemo(
    () => getTriggerPointsIfEnabled(TRIGGER_KEYS.PRIVATE_TC_SENT),
    [getTriggerPointsIfEnabled]
  );
  const perRecipientCfg = React.useMemo(
    () => getTriggerPointsIfEnabled(TRIGGER_KEYS.PRIVATE_TC_RECIPIENT),
    [getTriggerPointsIfEnabled]
  );
  const perNewRecipientCfg = React.useMemo(
    () => getTriggerPointsIfEnabled(TRIGGER_KEYS.PRIVATE_TC_NEW_RECIPIENT),
    [getTriggerPointsIfEnabled]
  );

  const anyEarningEnabled = React.useMemo(() => {
    if (triggersLoading) return true;
    return [insiderCfg.enabled, dropCfg.enabled, perRecipientCfg.enabled, perNewRecipientCfg.enabled].some(Boolean);
  }, [triggersLoading, insiderCfg.enabled, dropCfg.enabled, perRecipientCfg.enabled, perNewRecipientCfg.enabled]);

  React.useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const to = asInt(pointsTotal, 0);
    if (reduceMotion) {
      setPointsDisplay(to);
      return;
    }

    let raf = 0;
    const from = asInt(pointsDisplay, 0);
    if (from === to) return;

    const durationMs = 900;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let start = null;

    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / durationMs);
      const next = Math.floor(from + (to - from) * easeOutCubic(p));
      setPointsDisplay(next);

      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        const now = Date.now();
        if (now - lastBurstRef.current > 1000) {
          lastBurstRef.current = now;
          if (totalElRef.current) {
            party.confetti(totalElRef.current, {
              count: party.variation.range(35, 55),
              spread: 70,
              size: party.variation.range(0.9, 1.25),
              shapes: ["star"],
            });
          }
        }
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsTotal]);

  const pageBottomPad = "calc(env(safe-area-inset-bottom, 0px) + 32px)";

  const showHowToEarn = !loading && !err && receipts.length === 0;

  return (
    <Box sx={{ pt: 2.8, pb: pageBottomPad }}>
      <Container maxWidth="md" sx={{ pb: 3 }}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            borderRadius: 1,
            p: { xs: 2.1, sm: 2.7 },
            color: theme.colors.white,
            background: `linear-gradient(135deg, ${theme.colors.inssa} 0%, ${theme.colors.mediumBlue} 100%)`,
            position: "relative",
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(theme.colors.white, 0.16),
          })}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.6}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.06 }}>
                Your Momentum
              </Typography>
              <Typography sx={{ opacity: 0.92, mt: 0.5 }}>
                Turn everyday drops into points and perks.
              </Typography>
            </Box>
            <Box ref={totalElRef}>
              <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1, letterSpacing: -1 }}>
                {asInt(pointsDisplay, 0)}
              </Typography>
              <Typography sx={{ opacity: 0.9, fontWeight: 700 }}>total points earned</Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            {triggersLoading || profileLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} sx={(theme) => ({ color: alpha(theme.colors.white, 0.9) })} />
                <Typography sx={{ opacity: 0.9 }}>Loading ways to earn…</Typography>
              </Stack>
            ) : !anyEarningEnabled ? (
              <Alert
                severity="info"
                sx={(theme) => ({
                  backgroundColor: alpha(theme.colors.white, 0.92),
                  color: alpha(theme.palette.common.black, 0.85),
                })}
              >
                Points are currently paused. You can still bury capsules and explore as usual.
              </Alert>
            ) : (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {!isInsider && insiderCfg.enabled ? (
                  <Chip
                    icon={<WorkspacePremiumIcon sx={(theme) => ({ color: `${theme.colors.white} !important` })} />}
                    label={`Join Insider (+${asInt(insiderCfg.points, 0)})`}
                    onClick={() => navigate("/membership")}
                    clickable
                    sx={(theme) => ({
                      color: theme.colors.white,
                      border: "1px solid",
                      borderColor: alpha(theme.colors.white, 0.35),
                      bgcolor: alpha(theme.colors.white, 0.1),
                    })}
                  />
                ) : null}
                {dropCfg.enabled ? (
                  <Chip
                    icon={<PlaceIcon sx={(theme) => ({ color: `${theme.colors.white} !important` })} />}
                    label={`Drop +${asInt(dropCfg.points, 0)}`}
                    onClick={() => navigate("/")}
                    clickable
                    sx={(theme) => ({
                      color: theme.colors.white,
                      border: "1px solid",
                      borderColor: alpha(theme.colors.white, 0.35),
                      bgcolor: alpha(theme.colors.white, 0.1),
                    })}
                  />
                ) : null}
                {perRecipientCfg.enabled ? (
                  <Chip
                    icon={<StarsIcon sx={(theme) => ({ color: `${theme.colors.white} !important` })} />}
                    label={`Recipient +${asInt(perRecipientCfg.points, 0)}`}
                    sx={(theme) => ({
                      color: theme.colors.white,
                      border: "1px solid",
                      borderColor: alpha(theme.colors.white, 0.35),
                      bgcolor: alpha(theme.colors.white, 0.1),
                    })}
                  />
                ) : null}
                {perNewRecipientCfg.enabled ? (
                  <Chip
                    icon={<PersonAddAltIcon sx={(theme) => ({ color: `${theme.colors.white} !important` })} />}
                    label={`New recipient +${asInt(perNewRecipientCfg.points, 0)}`}
                    sx={(theme) => ({
                      color: theme.colors.white,
                      border: "1px solid",
                      borderColor: alpha(theme.colors.white, 0.35),
                      bgcolor: alpha(theme.colors.white, 0.1),
                    })}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        </Paper>

        <Grid container spacing={1.6} sx={{ mt: 0.4 }}>
          <Grid item xs={12}>
            <StatCard label="Activity entries" value={`${ledgerItems.length}`} icon={<EmojiEventsRoundedIcon fontSize="small" />} />
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          onClick={() => setPromoOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setPromoOpen(true);
            }
          }}
          sx={(theme) => ({
            mt: 1.6,
            p: 2,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            cursor: "pointer",
            border: "1px solid",
            borderColor: "divider",
            transition: "border-color 120ms ease, background-color 120ms ease",
            "&:hover": {
              borderColor: alpha(theme.colors.blue, 0.5),
              backgroundColor: alpha(theme.colors.blue, theme.palette.mode === "dark" ? 0.08 : 0.04),
            },
          })}
        >
          <Box
            sx={(theme) => ({
              width: 40,
              height: 40,
              flex: "0 0 auto",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(150deg, ${theme.colors.blue}, ${alpha(theme.colors.blue, 0.72)})`,
            })}
          >
            <WorkspacePremiumIcon sx={(theme) => ({ color: theme.colors.white, fontSize: 22 })} />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>Enter a promo code</Typography>
            <Typography sx={{ opacity: 0.75, fontSize: "0.85rem" }}>
              Have a code? Redeem it for bonus Insider Points.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 1.9 }}>
          {loading ? (
            <Paper elevation={0} sx={{ borderRadius: 1, p: 3.5 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : err ? (
            <Alert severity="error">{err}</Alert>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ p: 2.4 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {showHowToEarn ? "How To Earn Points" : "Recent Activity"}
                </Typography>
                <Typography sx={{ opacity: 0.75 }}>
                  {showHowToEarn
                    ? "Quick wins you can do right now."
                    : "A live history of what you did and what you earned."}
                </Typography>
              </Box>
              <Divider />

              {showHowToEarn ? (
                <Box sx={{ p: 2.4 }}>
                  {triggersLoading || profileLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : !anyEarningEnabled ? (
                    <Alert severity="info">Points are paused for now. Check back for the next season of rewards.</Alert>
                  ) : (
                    <Stack spacing={1.4}>
                      {!isInsider && insiderCfg.enabled ? (
                        <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 1 }}>
                          <Typography sx={{ fontWeight: 800 }}>
                            Join Insider and earn {asInt(insiderCfg.points, 0)} points
                          </Typography>
                          <Typography sx={{ opacity: 0.8, mt: 0.4 }}>
                            Activate your Insider status and start your account with a points boost.
                          </Typography>
                          <Button variant="contained" sx={{ mt: 1.1 }} onClick={() => navigate("/membership")}>
                            Activate Insider points
                          </Button>
                        </Paper>
                      ) : null}

                      {dropCfg.enabled ? (
                        <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 1 }}>
                          <Typography sx={{ fontWeight: 800 }}>
                            Bury a time capsule and earn {asInt(dropCfg.points, 0)} points
                          </Typography>
                          <Typography sx={{ opacity: 0.8, mt: 0.4 }}>
                            Drop something meaningful on the map and grow your score.
                          </Typography>
                          <Button variant="outlined" sx={{ mt: 1.1 }} onClick={() => navigate("/")}>
                            Go to Map
                          </Button>
                        </Paper>
                      ) : null}

                      {perRecipientCfg.enabled ? (
                        <Alert severity="success" sx={{ borderRadius: 1 }}>
                          Earn {asInt(perRecipientCfg.points, 0)} points for every recipient you include.
                        </Alert>
                      ) : null}
                      {perNewRecipientCfg.enabled ? (
                        <Alert severity="success" sx={{ borderRadius: 1 }}>
                          Earn {asInt(perNewRecipientCfg.points, 0)} bonus points for each new recipient.
                        </Alert>
                      ) : null}
                    </Stack>
                  )}
                </Box>
              ) : (
                <List disablePadding sx={{ pb: "env(safe-area-inset-bottom)" }}>
                  {ledgerItems.slice(0, visibleCount).map((r, idx, shown) => {
                    const d = r.isGroup ? describeCapsuleGroup(r) : describeReceipt(r);
                    const when = fmtDate(toDateMaybe(r.createdAt));
                    return (
                      <React.Fragment key={r.id}>
                        <ListItem sx={{ py: 2, px: 2.2, alignItems: "flex-start" }}>
                          <ListItemAvatar>
                            <Avatar
                              sx={(theme) => ({
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? alpha(theme.colors.green, 0.2)
                                    : theme.colors.mediumBlue,
                                color:
                                  theme.palette.mode === "dark"
                                    ? theme.colors.green
                                    : theme.colors.white,
                              })}
                            >
                              {d.icon}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            secondaryTypographyProps={{ component: "div" }}
                            primary={
                              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                                <Typography sx={{ fontWeight: 800 }}>{d.title}</Typography>
                                <Typography
                                  sx={(theme) => ({
                                    fontWeight: 900,
                                    color: theme.palette.mode === "dark" ? theme.colors.green : theme.colors.mediumBlue,
                                  })}
                                >
                                  +{asInt(d.pts, 0)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {d.subtitle ? (
                                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    {d.subtitle}
                                  </Typography>
                                ) : null}
                                {when ? (
                                  <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mt: 0.5 }}>
                                    {when}
                                  </Typography>
                                ) : null}
                              </Box>
                            }
                          />
                        </ListItem>
                        {idx < shown.length - 1 ? <Divider /> : null}
                      </React.Fragment>
                    );
                  })}
                  {ledgerItems.length > visibleCount ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      >
                        Show more ({ledgerItems.length - visibleCount})
                      </Button>
                    </Box>
                  ) : null}
                </List>
              )}
            </Paper>
          )}
        </Box>
      </Container>

      <PromoRedeemModal open={promoOpen} code="" onClose={() => setPromoOpen(false)} />
    </Box>
  );
}
